"use client";
import React, { useState, useEffect } from "react";
import {
  useGetGradeSystemQuery,
  useUpdateGradeMutation,
} from "../hooks/useAdmin";
import BackButton from "../components/backButton";
import { TopAdminNav } from "../layouts/TopAdminNav";
import { RegisterCard } from "../components/RegisterCard";
import {
  FaEdit,
  FaSave,
  FaTrash,
  FaPlus,
  FaCalendarAlt,
  FaSlidersH,
  FaLock,
} from "react-icons/fa";
import loader from "../assets/loader.gif";
import toast from "react-hot-toast";

function CurriculumSystem() {
  const { data: gradez, isLoading } = useGetGradeSystemQuery();
  const gradingData = gradez?.data?.[0];
  const updateGrade = useUpdateGradeMutation();

  // state
  const [editing, setEditing] = useState(false);
  const [curriculums, setCurriculums] = useState<any[]>([
    { name: "CA1", weight: 0.0 },
    { name: "CA2", weight: 0.0 },
    { name: "CA3", weight: 0.0 },
  ]);
  const [examWeight, setExamWeight] = useState<any>(0.0);
  const [curriculumInputs, setCurriculumInputs] = useState<string[]>([
    "0",
    "0",
    "0",
  ]);
  const [examInput, setExamInput] = useState("0");
  const [modifyStart, setModifyStart] = useState("");
  const [modifyEnd, setModifyEnd] = useState("");

  // initialize with backend data
  useEffect(() => {
    if (gradingData) {
      const caWeight = gradingData.ca_weight || 0;
      const exam = gradingData.exam_weight || 0;
      const numCas = gradingData.num_cas || 3;

      const perCa = numCas > 0 ? caWeight / numCas : 0;

      const defaultCAs = Array.from({ length: numCas }, (_, i) => ({
        name: `CA${i + 1}`,
        weight: perCa,
      }));

      setCurriculums(defaultCAs);
      setCurriculumInputs(
        defaultCAs.map((ca) => Number((ca.weight * 100).toFixed(2)).toString()),
      );
      setExamWeight(exam);
      setExamInput(Number((exam * 100).toFixed(2)).toString());
      setModifyStart(gradingData.ca_modify_start?.slice(0, 10) || "");
      setModifyEnd(gradingData.ca_modify_end?.slice(0, 10) || "");
    }
  }, [gradingData]);

  // === Handlers ===
  const normalizePercentInput = (value: string) => {
    if (value === "") return "";
    const parsed = Number(value);
    return Number.isNaN(parsed) ? "" : parsed / 100;
  };

  const handleCurriculumChange = (index: number, value: string) => {
    setCurriculumInputs((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });

    setCurriculums((prev) => {
      const updated = [...prev];
      updated[index].weight = normalizePercentInput(value);
      return updated;
    });
  };

  const handleAddCurriculum = () => {
    setCurriculums((prev) => [
      ...prev,
      { name: `CA${prev.length + 1}`, weight: 0.0 },
    ]);
    setCurriculumInputs((prev) => [...prev, ""]);
  };

  const handleRemoveCurriculum = (index: number) => {
    setCurriculums((prev) => prev.filter((_, i) => i !== index));
    setCurriculumInputs((prev) => prev.filter((_, i) => i !== index));
  };

  const handleExamWeightChange = (value: string) => {
    setExamInput(value);
    setExamWeight(normalizePercentInput(value));
  };

  const toggleEdit = () => {
    if (editing) {
      const hasEmptyAllocation =
        curriculums.some((c) => c.weight === "") || examWeight === "";

      if (hasEmptyAllocation) {
        toast.error("Please enter all allocation values before saving");
        return;
      }

      const total =
        curriculums.reduce((sum, c) => sum + Number(c.weight || 0), 0) +
        Number(examWeight || 0);

      if (Math.abs(total - 1) > 0.001) {
        toast.error("Total structural allocation sum must equal exactly 100%");
        return;
      }

      let payload = {
        num_cas: curriculums.length,
        ca_weight: curriculums.reduce(
          (sum, c) => sum + Number(c.weight || 0),
          0,
        ),
        exam_weight: Number(examWeight || 0),
        ca_modify_start: modifyStart,
        ca_modify_end: modifyEnd,
      };

      updateGrade.mutate(
        { payload },
        {
          onSuccess: () => {
            setEditing(false);
            setCurriculumInputs(
              curriculums.map((ca) =>
                Number((Number(ca.weight) * 100).toFixed(2)).toString(),
              ),
            );
            setExamInput(
              Number((Number(examWeight) * 100).toFixed(2)).toString(),
            );
          },
        },
      );
    } else {
      setEditing(true);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center m-4 h-screen bg-white">
        <img src={loader} alt="loader" className="w-16 h-16 object-contain" />
      </div>
    );
  }

  // Calculate Running Total Matrix Metric Realtime
  const runtimeTotalCalculated = Math.round(
    (curriculums.reduce((sum, c) => sum + Number(c.weight || 0), 0) +
      Number(examWeight || 0)) *
      100,
  );

  return (
    <div className="relative min-h-screen bg-slate-50/50 p-6 space-y-6">
      {/* Structural Admin Navbar Top Header Section */}
      <div className="w-full flex items-center gap-2">
        <BackButton />
        <TopAdminNav name="Jane Doe" title="Curriculum System Matrix" />
      </div>

      {/* Main Core Meta Banner View Entry Card Component */}
      <div className="w-full">
        <RegisterCard
          title="Curriculum Weights Registry"
          details="Configure systemic balance distribution formulas between assessment and exams"
        />
      </div>

      {/* Grid Layout Container System for Forms & Controls */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Left Column Area: Table Framework Matrix Grid Core */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex flex-sm items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-[#0096D1]/10 text-[#0096D1] flex items-center justify-center">
                <FaSlidersH className="text-sm" />
              </div>
              <div>
                <h2 className="text-[15px] font-extrabold text-slate-800 tracking-tight">
                  Weight Allocation Matrix
                </h2>
                <p className="text-[11px] font-medium text-slate-400">
                  Manage proportional parameter values
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={toggleEdit}
              className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-xs font-bold shadow-sm transition-all active:scale-95 ${
                editing
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                  : "bg-[#0096D1] hover:bg-[#007ba8] text-white"
              }`}
            >
              {editing ? (
                <>
                  <FaSave className="text-xs" /> Save Changes
                </>
              ) : (
                <>
                  <FaEdit className="text-xs" /> Adjust Settings
                </>
              )}
            </button>
          </div>

          <div className="overflow-x-auto w-full">
            <table className="w-full border-collapse text-left table-fixed min-w-[500px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100 text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                  <th className="py-4 px-6 w-[50%]">
                    Assessment Classification
                  </th>
                  <th className="py-4 px-4 w-[30%]">Max Allocatable Value</th>
                  {editing && (
                    <th className="py-4 px-4 w-[20%] text-center">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {curriculums?.map((ca, idx) => (
                  <tr
                    key={idx}
                    className="group bg-white hover:bg-slate-50/30 transition-colors text-[13px] font-semibold text-slate-600"
                  >
                    <td className="py-4 px-6 text-slate-800 tracking-tight font-bold capitalize">
                      {ca.name} Parameter
                    </td>
                    <td className="py-2 px-4">
                      {editing ? (
                        <div className="relative max-w-[140px]">
                          <input
                            type="number"
                            onWheel={(e) => e.currentTarget.blur()}
                            onKeyDown={(e) => {
                              if (e.key === "ArrowUp" || e.key === "ArrowDown")
                                e.preventDefault();
                            }}
                            value={curriculumInputs[idx] ?? ""}
                            onChange={(e) =>
                              handleCurriculumChange(idx, e.target.value)
                            }
                            className="w-full text-sm font-bold px-3 py-1.5 bg-slate-50/60 rounded-lg border border-slate-200 text-slate-800 focus:outline-none focus:bg-white focus:border-[#0096D1] focus:ring-1 focus:ring-[#0096D1] transition-all pr-8"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                            %
                          </span>
                        </div>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md font-mono font-bold bg-[#0096D1]/5 text-[#0096D1] text-xs">
                          {(ca.weight * 100).toFixed(0)}%
                        </span>
                      )}
                    </td>
                    {editing && (
                      <td className="py-2 px-4 text-center">
                        <button
                          type="button"
                          onClick={() => handleRemoveCurriculum(idx)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 active:scale-95 transition-all mx-auto"
                        >
                          <FaTrash className="text-xs" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}

                {/* Exam Core Ground Row Static Item */}
                <tr className="bg-slate-50/40 text-[13px] font-bold text-slate-700">
                  <td className="py-4 px-6 text-slate-800 tracking-tight font-black">
                    Comprehensive Term Exam
                  </td>
                  <td className="py-2 px-4">
                    {editing ? (
                      <div className="relative max-w-[140px]">
                        <input
                          type="number"
                          onWheel={(e) => e.currentTarget.blur()}
                          onKeyDown={(e) => {
                            if (e.key === "ArrowUp" || e.key === "ArrowDown")
                              e.preventDefault();
                          }}
                          value={examInput}
                          onChange={(e) =>
                            handleExamWeightChange(e.target.value)
                          }
                          className="w-full text-sm font-bold px-3 py-1.5 bg-slate-50/60 rounded-lg border border-slate-200 text-slate-800 focus:outline-none focus:bg-white focus:border-[#0096D1] focus:ring-1 focus:ring-[#0096D1] transition-all pr-8"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                          %
                        </span>
                      </div>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md font-mono font-bold bg-[#012063]/5 text-[#012063] text-xs">
                        {(examWeight * 100).toFixed(0)}%
                      </span>
                    )}
                  </td>
                  {editing && (
                    <td className="py-2 px-4 text-center text-slate-400 text-xs font-bold italic select-none">
                      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-slate-100 border border-slate-200 font-sans tracking-tight text-slate-400">
                        <FaLock className="text-[9px]" /> Static
                      </span>
                    </td>
                  )}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Interactive Table Bottom Operations Meta Bar */}
          <div className="p-4 bg-slate-50/30 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4">
            <div>
              {editing && (
                <button
                  type="button"
                  onClick={handleAddCurriculum}
                  className="inline-flex items-center justify-center gap-1.5 rounded-xl px-4 py-2 bg-slate-100 border border-slate-200 text-slate-700 text-xs font-bold hover:bg-slate-200/70 active:scale-95 transition-all"
                >
                  <FaPlus className="text-[10px]" /> Add Continuous Assessment
                </button>
              )}
            </div>

            {/* Matrix Realtime Calculation Summary Monitor */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                Total Metric Weight:
              </span>
              <span
                className={`px-3 py-1 rounded-lg font-mono text-xs font-black border ${
                  runtimeTotalCalculated === 100
                    ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                    : "bg-amber-50 border-amber-200 text-amber-700 animate-pulse"
                }`}
              >
                {runtimeTotalCalculated}% / 100%
              </span>
            </div>
          </div>
        </div>

        {/* Right Column Area: Date Control Modification Period Panel Block */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden p-5 space-y-4">
          <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
            <div className="h-9 w-9 rounded-xl bg-[#012063]/5 text-[#012063] flex items-center justify-center">
              <FaCalendarAlt className="text-sm" />
            </div>
            <div>
              <h3 className="text-[15px] font-extrabold text-slate-800 tracking-tight">
                Modification Window
              </h3>
              <p className="text-[11px] font-medium text-slate-400">
                Allowed edit accessibility range
              </p>
            </div>
          </div>

          {/* Form Date Fields Block Setup Matrix Elements */}
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
                Start Modification Bounds
              </label>
              <input
                type="date"
                value={modifyStart}
                // disabled={!editing}
                onChange={(e) => setModifyStart(e.target.value)}
                className="w-full text-sm font-semibold px-3 py-2.5 bg-slate-50/50 text-slate-700 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
              />
            </div>

            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
                End Expiration Bounds
              </label>
              <input
                type="date"
                value={modifyEnd}
                //disabled={!editing}
                onChange={(e) => setModifyEnd(e.target.value)}
                className="w-full text-sm font-semibold px-3 py-2.5 bg-slate-50/50 text-slate-700 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
              />
            </div>
          </div>

          {!editing && (
            <div className="text-[11px] font-medium leading-relaxed text-slate-400 bg-slate-50 border border-slate-100 p-3 rounded-xl">
              ⚠️ Content modification parameters are currently securely locked.
              Toggle{" "}
              <strong className="text-slate-600 font-bold">
                Adjust Settings
              </strong>{" "}
              to modify active periods.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CurriculumSystem;
